import React, { useState, useEffect } from 'react';
import {
  Star,
  ShieldCheck,
  ShieldAlert,
  MapPin,
  Clock,
  Phone,
  Mail,
  Award,
  Calendar,
  CheckCircle2,
  ArrowLeft,
  Briefcase,
  MessageSquare,
  Wrench,
  Lock,
  FileCheck,
  Camera,
  Map,
  AlertCircle,
  Search,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { VerificationBadge } from '../../components/common/VerificationBadge';
import { CategoryIcon } from '../../components/common/CategoryIcon';
import { ProfessionalReviewsSection } from '../../components/professional/ProfessionalReviewsSection';
import { ReportProfessionalModal } from '../../components/common/ReportProfessionalModal';
import { fetchPublicProfessionalProfile } from '../../lib/supabaseService';
import { updateDocumentSEO } from '../../utils/seo';
import { Profile } from '../../types';

export const ProfessionalProfilePage: React.FC = () => {
  const { profiles, reviews, jobs, quotes, messages, viewParams, navigateTo, currentUser } = useApp();
  const [showReportModal, setShowReportModal] = useState<boolean>(false);

  // Extract requested professional ID from viewParams or URL search parameters
  const requestedProId = (
    viewParams?.proId ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('proId') ||
        new URLSearchParams(window.location.search).get('id') ||
        new URLSearchParams(window.location.search).get('professionalId')
      : '') ||
    ''
  ).trim();

  // Initial lookup in local memory/cache to render immediately if available
  const initialLocalPro = requestedProId ? profiles.find((p) => p.id === requestedProId) || null : null;

  const [pro, setPro] = useState<Profile | null>(initialLocalPro);
  const [loading, setLoading] = useState<boolean>(!initialLocalPro && Boolean(requestedProId));
  const [notFound, setNotFound] = useState<boolean>(!requestedProId);

  // Fetch authentic record from Supabase / server API by exact professional ID
  useEffect(() => {
    let isMounted = true;

    if (!requestedProId) {
      setPro(null);
      setNotFound(true);
      setLoading(false);
      return;
    }

    const loadProfile = async () => {
      // Check local profiles cache first
      const cached = profiles.find((p) => p.id === requestedProId);
      if (cached) {
        setPro(cached);
        setNotFound(false);
      } else {
        setLoading(true);
      }

      try {
        const fetchedPro = await fetchPublicProfessionalProfile(requestedProId);
        if (!isMounted) return;

        if (fetchedPro && (fetchedPro.role === 'professional' || !fetchedPro.role)) {
          setPro(fetchedPro);
          setNotFound(false);
        } else if (!cached) {
          // If not in cache and not returned by Supabase / API, strictly mark not found
          // NEVER substitute Emeka Okonkwo or another user!
          setPro(null);
          setNotFound(true);
        }
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setPro(null);
          setNotFound(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [requestedProId, profiles]);

  // Synchronize page title and meta description with the loaded professional's own record
  useEffect(() => {
    if (pro && pro.fullName) {
      const loc = [pro.lga, pro.state].filter(Boolean).join(', ') || 'Nigeria';
      const trade = (pro.primaryCategory || 'Artisan').replace(/-/g, ' ');
      updateDocumentSEO(
        'pro_profile',
        {
          title: `${pro.fullName} – Verified ${trade} in ${loc} | JobMatch NG`,
          description: `View ${pro.fullName}'s verified artisan profile, ${trade} specialties, diagnostic rates, and customer reviews in ${loc} on JobMatch NG.`,
          canonicalPath: '/professional-profile',
          noindex: true,
        },
        viewParams
      );
    } else if (notFound) {
      updateDocumentSEO(
        'pro_profile',
        {
          title: 'Professional Profile Unavailable | JobMatch NG',
          description: 'The requested professional profile is unavailable on JobMatch NG.',
          canonicalPath: '/professional-profile',
          noindex: true,
        },
        viewParams
      );
    }
  }, [pro, notFound, viewParams]);

  // Loading State
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-3xl p-10 border border-slate-200 shadow-sm text-center space-y-4">
          <div className="w-10 h-10 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <h2 className="text-base font-bold text-slate-800">Loading Professional Profile...</h2>
          <p className="text-xs text-slate-500">Retrieving verified artisan details from JobMatch NG registry.</p>
        </div>
      </div>
    );
  }

  // Not Found / Unavailable Profile State (NEVER substitute another user!)
  if (notFound || !pro) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-6 text-center">
        <div className="w-16 h-16 bg-slate-100 text-slate-400 rounded-3xl flex items-center justify-center mx-auto text-2xl font-bold border border-slate-200 shadow-xs">
          <AlertCircle className="w-8 h-8 text-slate-400" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-extrabold text-slate-900">Professional Profile Unavailable</h1>
          <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
            {requestedProId
              ? `We could not locate a verified professional profile with identifier "${requestedProId}". The profile may have been updated, deactivated, or does not exist.`
              : 'No professional was selected. Please search the verified artisan directory to view available profiles.'}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
          <button
            onClick={() => navigateTo('find_pro')}
            className="w-full sm:w-auto px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-colors flex items-center justify-center gap-2 cursor-pointer"
          >
            <Search className="w-4 h-4" />
            <span>Browse Verified Professionals</span>
          </button>
          <button
            onClick={() => navigateTo('home')}
            className="w-full sm:w-auto px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
          >
            Back to Homepage
          </button>
        </div>
      </div>
    );
  }

  // Find reviews for this specific professional
  const proReviews = reviews.filter((r) => r.professionalId === pro.id);
  const displayName = pro.fullName || 'Verified Artisan';
  const displayBusiness = pro.businessName || `${displayName} Technical Services`;
  const displayRating = typeof pro.rating === 'number' ? pro.rating.toFixed(1) : '5.0';
  const displayReviewCount = pro.reviewCount || proReviews.length || 0;
  const displayRate = pro.hourlyRate && pro.hourlyRate > 0 ? `₦${pro.hourlyRate.toLocaleString()}` : 'Contact for Estimate';
  const displayExperience = pro.yearsExperience !== undefined && pro.yearsExperience !== null ? pro.yearsExperience : 3;
  const displayLocation = [pro.cityArea, pro.lga, pro.state].filter(Boolean).join(', ') || 'Nigeria';

  // Service Areas list (or default to registered LGA/State)
  const serviceAreasList =
    pro.serviceAreas && pro.serviceAreas.length > 0
      ? pro.serviceAreas
      : [pro.lga, pro.state].filter(Boolean).length > 0
      ? [pro.lga, pro.state].filter(Boolean) as string[]
      : ['Local LGA Coverage'];

  // Trade Certifications list (or neutral placeholder)
  const certificationsList =
    pro.tradeCertifications && pro.tradeCertifications.length > 0
      ? pro.tradeCertifications
      : ['Verified Trade Experience & KYC Registered'];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* Back button */}
      <button
        onClick={() => navigateTo('find_pro')}
        className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1.5 cursor-pointer"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Professional Directory</span>
      </button>

      {/* Main Profile Header Card */}
      <div className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-xl relative overflow-hidden">
        {/* Subtle accent bar */}
        <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500"></div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="relative shrink-0">
              {pro.avatarUrl ? (
                <img
                  src={pro.avatarUrl}
                  alt={displayName}
                  className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl object-cover ring-4 ring-emerald-50 shadow-md"
                  onError={(e) => {
                    // Fallback on image load error
                    (e.target as HTMLElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-emerald-100 text-emerald-800 font-black text-2xl flex items-center justify-center ring-4 ring-emerald-50 shadow-md">
                  {displayName.slice(0, 2).toUpperCase()}
                </div>
              )}
              {pro.isProfilePhotoVerified && (
                <div
                  title="Biometric Verified Photo - Captured via Live KYC"
                  className="absolute -bottom-2 -right-2 px-2.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-black shadow-md flex items-center gap-1 border-2 border-white tracking-tight"
                >
                  <Camera className="w-3 h-3" />
                  <span>Verified Photo</span>
                </div>
              )}
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                  {displayName}
                </h1>
                <VerificationBadge profile={pro} status={pro.verificationStatus} size="md" />
              </div>

              <p className="text-sm font-semibold text-emerald-800 mt-1">
                {displayBusiness}
              </p>

              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
                <span className="flex items-center gap-1 font-medium text-slate-700">
                  <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                  {displayLocation}
                </span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <strong className="text-slate-900">{displayRating}</strong>
                  <span>({displayReviewCount} reviews)</span>
                </span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Award className="w-3.5 h-3.5 text-slate-400" />
                  <span>{displayExperience} Years Experience</span>
                </span>
              </div>
            </div>
          </div>

          {/* Action Rates & Hire CTA */}
          <div className="flex flex-col sm:flex-row md:flex-col items-start md:items-end justify-between gap-3 border-t md:border-t-0 pt-4 md:pt-0">
            <div>
              <span className="text-[10px] uppercase text-slate-400 block md:text-right">Est. Diagnostic Rate</span>
              <span className="text-2xl font-extrabold text-emerald-800">
                {displayRate}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              {currentUser &&
                (currentUser.role === 'admin' ||
                  messages.some(
                    (m) =>
                      (m.senderId === currentUser.id && m.receiverId === pro.id) ||
                      (m.senderId === pro.id && m.receiverId === currentUser.id)
                  ) ||
                  jobs.some((j) => {
                    if (j.customerId !== currentUser.id) return false;
                    const meta = (j.aiScopeEstimate as any)?.lifecycle_meta || {};
                    const hiredProId =
                      j.selectedProfessionalId ||
                      (j as any).selected_professional_id ||
                      meta.selected_professional_id ||
                      meta.assigned_professional_id ||
                      meta.target_professional_id;
                    const hasAcceptedQuote = quotes.some(
                      (q) =>
                        q.jobId === j.id &&
                        q.professionalId === pro.id &&
                        q.status === 'accepted'
                    );
                    return hiredProId === pro.id || hasAcceptedQuote;
                  })) && (
                  <button
                    id="message-pro-directly-btn"
                    type="button"
                    onClick={() => {
                      navigateTo('messages', {
                        contactId: pro.id,
                        proId: pro.id,
                      });
                    }}
                    className="px-4 py-3 rounded-xl bg-white hover:bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold text-xs shadow-2xs transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>Message</span>
                  </button>
                )}

              <button
                id="request-quote-from-pro-btn"
                onClick={() =>
                  navigateTo('post_job', {
                    selectedCategory: pro.primaryCategory,
                    targetProfessionalId: pro.id,
                    targetProfessionalName: displayName,
                    targetProfessionalBusiness: displayBusiness,
                    targetProfessionalAvatar: pro.avatarUrl,
                  })
                }
                className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer"
              >
                <Briefcase className="w-4 h-4" />
                <span>Post Job &amp; Request Quote</span>
              </button>
            </div>
          </div>
        </div>

        {/* Bio */}
        <div className="mt-8 pt-6 border-t border-slate-100">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">About &amp; Craftsmanship</h3>
          <p className="text-xs sm:text-sm text-slate-700 leading-relaxed max-w-4xl">
            {pro.bio ||
              `Verified artisan providing professional ${pro.primaryCategory || 'technical'} services across ${
                pro.state || 'Nigeria'
              }. Dedicated to quality craftsmanship, honest diagnostic assessments, and safe job completion under JobMatch NG Escrow protection.`}
          </p>
        </div>

        {/* Skills & Specialties */}
        {((pro.specialties && pro.specialties.length > 0) || (pro.skills && pro.skills.length > 0)) && (
          <div className="mt-6 pt-4 border-t border-slate-100">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Verified Specialties &amp; Skills</h3>
            <div className="flex flex-wrap gap-1.5">
              {(pro.specialties && pro.specialties.length > 0 ? pro.specialties : pro.skills || []).map((skill) => (
                <span
                  key={skill}
                  className="px-3 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-100"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 2-Column Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Qualifications & Service Areas */}
        <div className="space-y-6">
          {/* Verification Box */}
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-4">
            <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>Identity &amp; Trust Verification</span>
            </h3>

            <div className="space-y-2.5 text-xs">
              <div className="flex items-center justify-between p-2 rounded-xl bg-emerald-50/60 border border-emerald-100">
                <span className="text-slate-600 flex items-center gap-1.5">
                  <FileCheck className="w-3.5 h-3.5 text-emerald-600" />
                  Government Identity Check:
                </span>
                <span className="font-bold text-emerald-800">
                  {pro.verificationStatus === 'verified' ? 'Verified by NIMC' : 'KYC Registered'}
                </span>
              </div>

              <div className="flex items-center justify-between p-2 rounded-xl bg-emerald-50/60 border border-emerald-100">
                <span className="text-slate-600 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                  Address Verification:
                </span>
                <span className="font-bold text-emerald-800">
                  {pro.verificationStatus === 'verified' ? 'Utility Document Passed' : 'Under Review'}
                </span>
              </div>

              <div className="flex items-center justify-between p-2 rounded-xl bg-emerald-50/60 border border-emerald-100">
                <span className="text-slate-600 flex items-center gap-1.5">
                  <Camera className="w-3.5 h-3.5 text-emerald-600" />
                  Biometric Liveness:
                </span>
                <span className="font-bold text-emerald-800">
                  {pro.isProfilePhotoVerified ? 'Real-time Verified' : 'Standard KYC'}
                </span>
              </div>

              {pro.isProfilePhotoVerified && (
                <div className="flex items-center justify-between p-2 rounded-xl bg-emerald-50/60 border border-emerald-100">
                  <span className="text-slate-600 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    Profile Photo:
                  </span>
                  <span className="font-bold text-emerald-800 text-[11px]">Live Selfie Locked</span>
                </div>
              )}

              <div className="flex flex-col p-2.5 rounded-xl bg-slate-50 border border-slate-100 gap-1">
                <span className="text-slate-600 font-medium">Trade Certifications:</span>
                <div className="space-y-1">
                  {certificationsList.map((cert, idx) => (
                    <div key={idx} className="font-bold text-slate-800 text-xs">
                      • {cert}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200 text-[11px] text-slate-500 flex items-start gap-2">
              <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
              <span>
                Under NDPA 2023 guidelines, sensitive home coordinates and government ID scans are strictly encrypted &amp; restricted to authorized verifiers.
              </span>
            </div>
          </div>

          {/* Service Areas Coverage */}
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-3">
            <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-600" />
              <span>Service Area Coverage</span>
            </h3>
            <p className="text-xs text-slate-500">
              This professional travels within these areas across {pro.state || 'Nigeria'}:
            </p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {serviceAreasList.map((area) => (
                <span
                  key={area}
                  className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>

          {/* Trust & Safety Reporting Box */}
          <div className="bg-rose-50/60 rounded-3xl p-5 border border-rose-200 shadow-xs space-y-2.5">
            <div className="flex items-center gap-2 text-rose-900 font-bold text-xs">
              <ShieldAlert className="w-4 h-4 text-rose-600" />
              <span>Trust &amp; Safety Guarantee</span>
            </div>
            <p className="text-[11px] text-rose-800 leading-relaxed">
              Have concerns about this professional's conduct, safety, or work agreement? Report anonymously to the JobMatch NG Trust Desk.
            </p>
            <button
              type="button"
              onClick={() => setShowReportModal(true)}
              className="w-full py-2 px-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs shadow-xs transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <ShieldAlert className="w-3.5 h-3.5" />
              <span>Report Professional / Conduct Concern</span>
            </button>
          </div>

          {/* Direct Communication Info */}
          <div className="bg-slate-900 text-white rounded-3xl p-6 shadow-xs space-y-3">
            <h3 className="font-bold text-sm text-white">Direct Communication</h3>
            <p className="text-xs text-slate-300">
              For security, phone and WhatsApp contact are revealed upon quote acceptance.
            </p>
            <div className="space-y-2 text-xs text-slate-300 pt-2 border-t border-slate-800">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-emerald-400" />
                <span>+234 80* *** **** (Verified Contact)</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-400" />
                <span>Official JobMatch NG Messaging</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Customer Reviews & Craftsmanship */}
        <div className="lg:col-span-2 space-y-6">
          {/* Craftsmanship Proof / Portfolio Showcase */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-emerald-600" />
                <span>Craftsmanship &amp; Work Proof</span>
              </h3>
              <span className="text-xs text-slate-400 font-medium">Escrow Protected</span>
            </div>

            {(pro as any).portfolioPhotos && Array.isArray((pro as any).portfolioPhotos) && (pro as any).portfolioPhotos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {((pro as any).portfolioPhotos as string[]).map((photoUrl: string, idx: number) => (
                  <div key={idx} className="rounded-2xl overflow-hidden aspect-video bg-slate-100 relative group border border-slate-200">
                    <img
                      src={photoUrl}
                      alt={`Craftsmanship work proof ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl p-6 bg-slate-50 border border-slate-200 space-y-2">
                <p className="text-xs text-slate-700 leading-relaxed">
                  Every artisan on JobMatch NG provides verified proof of work and on-site diagnostic assessments. When you post a repair job and receive a quote from <strong>{displayName}</strong>, milestone photos and itemized material breakdowns are shared directly inside the JobMatch NG workspace.
                </p>
                <div className="flex items-center gap-2 pt-1 text-[11px] text-emerald-800 font-semibold">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Backed by 100% Escrow Protection — Payment released only when you confirm satisfaction.</span>
                </div>
              </div>
            )}
          </div>

          {/* Reviews List & Aggregate Rating Breakdown */}
          <ProfessionalReviewsSection
            professionalId={pro.id}
            professionalName={displayName}
            initialRating={typeof pro.rating === 'number' ? pro.rating : 5.0}
            initialReviewCount={displayReviewCount}
          />
        </div>
      </div>

      {/* Safety Report Modal */}
      {showReportModal && (
        <ReportProfessionalModal
          professionalId={pro.id}
          professionalName={displayName}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
};
